import {SUNBURST_RATIOS} from '../../../../shared/sunburst';
import {NANO21_RATIOS} from '../../../../shared/nanoBanana21';
// Each selector has its own storage key. A stale tab must not replace another
// tab's per-chat preferences when it changes an unrelated model or resolution.
export const imageSelectorFields = ['inspire', 'inspireByProject', 'imageModel', 'imageModelUserSelected', 'imageModelByProject', 'imageTier', 'imageTierByProject', 'imageResolution', 'imageResolutionByProject', 'aspectRatiosByProject'] as const;
type Field = typeof imageSelectorFields[number];
type Selectors = Partial<Record<Field, unknown>>;
type SelectorStorage = Pick<Storage, 'getItem' | 'setItem' | 'key' | 'length'>;
const prefix = 'canvas-image-selectors-v2:';
const valid = (field: Field, value: unknown) => field === 'aspectRatiosByProject'
  ? Array.isArray(value) && value.length > 0 && value.length <= 20 && value.every(ratio => ratio === 'auto' || [...SUNBURST_RATIOS,...NANO21_RATIOS].includes(ratio)) && new Set(value).size === value.length && (!value.includes('auto') || value.length === 1)
  : field === 'imageModelUserSelected' || field.startsWith('inspire')
  ? typeof value === 'boolean'
  : field.startsWith('imageModel')
    ? ['nano-banana-2', 'nano-banana-2.1', 'gpt-image-2', 'gpt-image-2.5-sunburst'].includes(value as string)
    : field.startsWith('imageResolution')
      ? ['1k', '2k', '4k'].includes(value as string)
      : value === null || ['low', 'medium', 'high', 'xhigh', 'max', '1k', '2k', '4k'].includes(value as string);
const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
function read(storage: SelectorStorage, key: string): unknown {
  try { return JSON.parse(storage.getItem(key) || 'null'); } catch { return null; }
}

export function restoreImageSelectors(storage: SelectorStorage): Selectors {
  const saved = record(read(storage, 'canvas-image-selectors-v1'));
  const restored: Selectors = {};
  // Keep existing preferences until that particular selector is changed.
  for (const field of imageSelectorFields) {
    if (field.endsWith('ByProject')) {
      restored[field] = Object.fromEntries(Object.entries(record(saved[field])).filter(([id, value]) => /^\d+$/.test(id) && valid(field, value)));
    } else if (valid(field, saved[field])) restored[field] = saved[field];
  }
  restored.imageModelUserSelected = typeof restored.imageModel === 'string' ? saved.imageModelUserSelected !== false : false;
  if (restored.imageModel === 'gpt-image-2' && !saved.imageResolution) restored.imageResolution = '4k';
  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index);
    if (!key?.startsWith(prefix)) continue;
    const [rawField, id, extra] = key.slice(prefix.length).split(':');
    if (!imageSelectorFields.includes(rawField as Field) || extra !== undefined) continue;
    const field = rawField as Field;
    const item = record(read(storage, key));
    if (field.endsWith('ByProject')) {
      if (!id || !/^\d+$/.test(id)) continue;
      const entries = record(restored[field]);
      if (item.deleted === true) delete entries[id];
      else if (valid(field, item.value)) entries[id] = item.value;
      restored[field] = entries;
    } else if (id === undefined && valid(field, item.value)) restored[field] = item.value;
  }
  return restored;
}

export function persistImageSelectorChanges(storage: SelectorStorage, state: Selectors, previous: Selectors): void {
  for (const field of imageSelectorFields) {
    if (state[field] === previous[field]) continue;
    if (field.endsWith('ByProject')) {
      const before = record(previous[field]);
      const after = record(state[field]);
      for (const id of new Set([...Object.keys(before), ...Object.keys(after)])) {
        if (!/^\d+$/.test(id) || before[id] === after[id]) continue;
        if (!(id in after)) storage.setItem(`${prefix}${field}:${id}`, JSON.stringify({ deleted: true }));
        else if (valid(field, after[id])) storage.setItem(`${prefix}${field}:${id}`, JSON.stringify({ value: after[id] }));
      }
    } else if (valid(field, state[field])) storage.setItem(`${prefix}${field}`, JSON.stringify({ value: state[field] }));
  }
}
